import { t, type ErrorCodeMapperEntry } from "./types.js";

/** Error codes 30000-30999. One thousand-block per file: a duplicate number here can only
 *  collide within this file, which is what makes the split safer than one shared object. */
export const spacesErrors = {
  // 30xxx is the spaces module's series. Eleven of its sites needed no new code: they said
  // "space not found" in four different ways ("Unknown Space." x7, "Space does not exists" x3,
  // "No Space found" x1) and were all folded onto the existing SPACE_NOT_FOUND (7015,
  // "Space not found.") — the same call made for the projects module's project-not-found wordings.
  // One `noPermissionMsg` site moved to INSUFFICIENT_PERMISSION (7012), which spaces already used
  // at three other sites.
  // 30001-30010 — the spaces module's split.
  SPACE_NO_MATCH_NO_USER_REMOVED: {
    errorCode: 30001,
    errorMessage: t("No Space matched the request, so no user was removed."),
    isDynamic: false,
  },
  SPACE_ADD_USERS_FAILED: {
    errorCode: 30002,
    errorMessage: t("Something went wrong while adding users to the Space."),
    isDynamic: false,
  },
  SPACE_SOME_USERS_NOT_ADDED: {
    errorCode: 30003,
    errorMessage: t("Some user(s) could not be added. Verify that they have a Full Seat and try again."),
    isDynamic: false,
  },
  SPACE_UPDATE_USER_ACCESS_FAILED: {
    errorCode: 30004,
    errorMessage: t("Something went wrong while updating user access in the Space."),
    isDynamic: false,
  },
  // The Space-summary AI call: unparseable output versus parseable-but-wrong-shape.
  SPACE_AI_RESPONSE_UNPARSEABLE: {
    errorCode: 30005,
    errorMessage: t("AI response could not be parsed"),
    isDynamic: false,
  },
  SPACE_AI_RESPONSE_UNEXPECTED_FORMAT: {
    errorCode: 30006,
    errorMessage: t("AI response was not in the expected format"),
    isDynamic: false,
  },
  // 30007-30009 mirror the projects module's role codes, but deliberately keep ONE CODE PER WORDING
  // rather than harmonising the way projects did — so "Role id does not exist" stays on the wire for
  // Spaces even though projects no longer sends it.
  SPACE_ROLE_ID_NOT_EXIST: {
    errorCode: 30007,
    errorMessage: t("Role id does not exist"),
    isDynamic: false,
  },
  SPACE_ROLE_NOT_FOUND: {
    errorCode: 30008,
    errorMessage: t("That Space role no longer exists."),
    isDynamic: false,
  },
  SPACE_DEFAULT_ROLE_PROTECTED: {
    errorCode: 30009,
    errorMessage: t("Default role cannot be deleted"),
    isDynamic: false,
  },
  SPACE_SET_DEFAULT_FAILED: {
    errorCode: 30010,
    errorMessage: t("The Space could not be set as default."),
    isDynamic: false,
  },
  SPACE_NAME_REQUIRED: {
    errorCode: 30012,
    errorMessage: t("spaceName is required"),
    isDynamic: false,
  },
  SPACE_ID_REQUIRED: {
    errorCode: 30013,
    errorMessage: t("A space id is required."),
    isDynamic: false,
  },
  SPACE_ADD_USERS_NO_MEMBER_ROLE: {
    errorCode: 30014,
    errorMessage: t(
      "Users could not be added: the space has no SPACE_MEMBER role, or the acting account no longer exists.",
    ),
    isDynamic: false,
  },
  // The space twin of PROJECT_SEAT_LIMIT_REACHED (20003). `space.service.ts` used to answer BOTH the
  // seat refusal and the no-permission refusal with INSUFFICIENT_PERMISSION, telling them apart only
  // by a ternary on `errorMessage` — so dropping the message would have reported a seat problem as a
  // permission problem.
  SPACE_SEAT_LIMIT_REACHED: {
    errorCode: 30015,
    errorMessage: t("Space could not be created. Note: Your seat does not allow you to perform this action."),
    isDynamic: false,
  },
  // 30016: three push-notification exits that named the space refusal only in prose.
  SPACE_UNKNOWN: {
    errorCode: 30016,
    errorMessage: t("Unknown Space."),
    isDynamic: false,
  },
} satisfies Record<string, ErrorCodeMapperEntry>;
